import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Layout';
import { api } from '../hooks/useApi';
import { clsx } from 'clsx';
import {
  Calendar, Clock, Play, Pause, Save, RefreshCw,
  BatteryCharging, Zap, ToggleLeft, ToggleRight, Settings, Layers
} from 'lucide-react';

const ACTION_CONFIG = {
  charge: { label: 'Laadimine', color: 'green', bgClass: 'bg-green-500', icon: BatteryCharging },
  discharge: { label: 'Tühjendamine', color: 'red', bgClass: 'bg-red-500', icon: Zap },
  standby: { label: 'Omatarve', color: 'blue', bgClass: 'bg-blue-500', icon: Pause }
};

const TEMPLATES = [
  { id: 'omatarve', name: 'Omatarve', description: 'Kõik ajapesad omatarbe režiimis' },
  { id: 'night_charge', name: 'Öine laadimine', description: '00:00-06:00 laadimine' },
  { id: 'peak_shaving', name: 'Tipukoormuse vähendamine', description: 'Tühjendamine tipuajal' },
  { id: 'solar_storage', name: 'Päikese salvestamine', description: '10:00-16:00 laadimine' }
];

function TimeSlotGrid({ slots, currentSlotIndex, selectedSlots, onSlotClick, onSlotDragStart, onSlotDragEnter, onSlotDragEnd }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-400">24h ajakava (15-min intervallid)</h3>
        <div className="flex gap-4 text-xs">
          {Object.entries(ACTION_CONFIG).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={clsx('w-3 h-3 rounded', config.bgClass)} />
              <span className="text-gray-400">{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Hour labels */}
      <div className="flex mb-1">
        <div className="w-12" />
        {Array.from({ length: 24 }, (_, i) => (
          <div
            key={i}
            className="flex-1 text-center text-[10px] text-gray-500"
          >
            {i.toString().padStart(2, '0')}
          </div>
        ))}
      </div>

      {/* Time slots grid - 4 rows (one per 15-min interval) */}
      {[0, 1, 2, 3].map(row => (
        <div key={row} className="flex">
          <div className="w-12 text-[10px] text-gray-500 flex items-center">
            :{(row * 15).toString().padStart(2, '0')}
          </div>
          {Array.from({ length: 24 }, (_, hour) => {
            const slotIndex = hour * 4 + row;
            const slot = slots[slotIndex];
            const isCurrent = slotIndex === currentSlotIndex;
            const isSelected = selectedSlots.includes(slotIndex);
            const config = ACTION_CONFIG[slot?.action] || ACTION_CONFIG.standby;

            return (
              <div
                key={slotIndex}
                className={clsx(
                  'flex-1 h-8 border border-gray-700 cursor-pointer transition-all',
                  config.bgClass,
                  isCurrent ? 'ring-2 ring-white ring-inset' : '',
                  isSelected ? 'ring-2 ring-yellow-400 ring-inset opacity-80' : 'opacity-60 hover:opacity-100'
                )}
                onClick={() => onSlotClick(slotIndex)}
                onMouseDown={() => onSlotDragStart(slotIndex)}
                onMouseEnter={(e) => e.buttons === 1 && onSlotDragEnter(slotIndex)}
                onMouseUp={onSlotDragEnd}
                title={`${slot?.startTime} - ${slot?.name || config.label}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function SlotEditor({ selectedSlots, slots, onSave, onCancel }) {
  const firstSlot = slots[selectedSlots[0]] || {};
  const [settings, setSettings] = useState({
    action: firstSlot.action || 'standby',
    targetSoc: firstSlot.targetSoc || 15,
    maxPower: firstSlot.maxPower || 100,
    gridCharge: firstSlot.gridCharge ?? true,
    sellToGrid: firstSlot.sellToGrid ?? false,
    name: firstSlot.name || 'Omatarve'
  });

  const getTimeRange = () => {
    if (selectedSlots.length === 0) return '';
    const sorted = [...selectedSlots].sort((a, b) => a - b);
    const startSlot = slots[sorted[0]];
    const endSlot = slots[sorted[sorted.length - 1]];
    return `${startSlot?.startTime} - ${endSlot?.endTime}`;
  };

  const handleSave = () => {
    onSave(selectedSlots, settings);
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-medium">Muuda ajapesi</h3>
          <p className="text-sm text-gray-400">
            {selectedSlots.length} pesä valitud ({getTimeRange()})
          </p>
        </div>
        <Settings className="w-5 h-5 text-gray-400" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        {/* Action */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Tegevus</label>
          <select
            value={settings.action}
            onChange={(e) => setSettings(s => ({ ...s, action: e.target.value }))}
            className="input w-full"
          >
            <option value="standby">Omatarve</option>
            <option value="charge">Laadimine</option>
            <option value="discharge">Tühjendamine</option>
          </select>
        </div>

        {/* Target SOC */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Siht-SOC (%)</label>
          <input
            type="number"
            value={settings.targetSoc}
            onChange={(e) => setSettings(s => ({ ...s, targetSoc: parseInt(e.target.value) || 0 }))}
            className="input w-full"
            min="0"
            max="100"
          />
        </div>

        {/* Max Power */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Max võimsus (kW)</label>
          <input
            type="number"
            value={settings.maxPower}
            onChange={(e) => setSettings(s => ({ ...s, maxPower: parseInt(e.target.value) || 0 }))}
            className="input w-full"
            min="0"
            max="125"
          />
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Nimi</label>
          <input
            type="text"
            value={settings.name}
            onChange={(e) => setSettings(s => ({ ...s, name: e.target.value }))}
            className="input w-full"
          />
        </div>

        {/* Grid Charge */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.gridCharge}
              onChange={(e) => setSettings(s => ({ ...s, gridCharge: e.target.checked }))}
              className="w-4 h-4 rounded bg-gray-700 border-gray-600"
            />
            <span className="text-sm text-gray-300">Võrgust laadimine</span>
          </label>
        </div>

        {/* Sell to Grid */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sellToGrid}
              onChange={(e) => setSettings(s => ({ ...s, sellToGrid: e.target.checked }))}
              className="w-4 h-4 rounded bg-gray-700 border-gray-600"
            />
            <span className="text-sm text-gray-300">Võrku müük</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="btn btn-secondary">
          Tühista
        </button>
        <button onClick={handleSave} className="btn btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          Salvesta ({selectedSlots.length} pesä)
        </button>
      </div>
    </div>
  );
}

function TemplateSelector({ onApply, loading }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-400">Mallid</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {TEMPLATES.map(template => (
          <button
            key={template.id}
            onClick={() => onApply(template.id)}
            disabled={loading}
            className="p-3 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-colors text-left"
          >
            <div className="text-sm text-white font-medium">{template.name}</div>
            <div className="text-xs text-gray-400 mt-0.5">{template.description}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CurrentStatus({ currentSlot, decision, soc }) {
  if (!currentSlot) return null;

  const config = ACTION_CONFIG[currentSlot.action] || ACTION_CONFIG.standby;
  const Icon = config.icon;

  return (
    <div className="card p-4 border-l-4 border-primary-500">
      <div className="flex items-center gap-4">
        <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center', `${config.bgClass}/20`)}>
          <Icon className={clsx('w-6 h-6', `text-${config.color}-400`)} />
        </div>
        <div className="flex-1">
          <div className="text-sm text-gray-400">Praegune ajapesa ({currentSlot.startTime})</div>
          <div className="text-white font-medium">{currentSlot.name || config.label}</div>
        </div>
        {decision && (
          <div className="text-right">
            <div className="text-sm text-gray-400">Otsus</div>
            <div className="text-white">{decision.reason}</div>
          </div>
        )}
        {soc !== null && (
          <div className="text-right">
            <div className="text-sm text-gray-400">SOC</div>
            <div className="text-2xl font-bold text-white">{soc?.toFixed(0)}%</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SchedulePage({ wsData, wsConnected }) {
  const [data, setData] = useState({ enabled: false, timeSlots: [], currentSlotIndex: 0 });
  const [activeData, setActiveData] = useState({ active: null, decision: null, currentSoc: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [dragStart, setDragStart] = useState(null);

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const [scheduleData, activeSchedule] = await Promise.all([
        api.getSchedules(),
        api.getActiveSchedule()
      ]);
      setData(scheduleData);
      setActiveData(activeSchedule);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedules();
    const interval = setInterval(loadSchedules, 30000);
    return () => clearInterval(interval);
  }, [loadSchedules]);

  const toggleScheduler = async () => {
    try {
      await api.enableScheduler(!data.enabled);
      loadSchedules();
    } catch (err) {
      console.error('Failed to toggle scheduler:', err);
    }
  };

  const handleSlotClick = (index) => {
    if (selectedSlots.includes(index)) {
      setSelectedSlots(selectedSlots.filter(i => i !== index));
    } else {
      setSelectedSlots([...selectedSlots, index]);
    }
  };

  const handleSlotDragStart = (index) => {
    setDragStart(index);
    setSelectedSlots([index]);
  };

  const handleSlotDragEnter = (index) => {
    if (dragStart === null) return;

    const start = Math.min(dragStart, index);
    const end = Math.max(dragStart, index);
    const newSelection = [];
    for (let i = start; i <= end; i++) {
      newSelection.push(i);
    }
    setSelectedSlots(newSelection);
  };

  const handleSlotDragEnd = () => {
    setDragStart(null);
  };

  const handleSaveSlots = async (slots, settings) => {
    try {
      setSaving(true);
      const sortedSlots = [...slots].sort((a, b) => a - b);

      // Check if consecutive slots - use range update
      let isConsecutive = true;
      for (let i = 1; i < sortedSlots.length; i++) {
        if (sortedSlots[i] !== sortedSlots[i - 1] + 1) {
          isConsecutive = false;
          break;
        }
      }

      if (isConsecutive && sortedSlots.length > 1) {
        await api.updateSlotRange(sortedSlots[0], sortedSlots[sortedSlots.length - 1], settings);
      } else {
        const slotUpdates = sortedSlots.map(index => ({ index, ...settings }));
        await api.updateSlots(slotUpdates);
      }

      setSelectedSlots([]);
      await loadSchedules();
    } catch (err) {
      console.error('Failed to save slots:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    if (!confirm(`Kas oled kindel? See asendab kõik praegused seaded malliga "${templateId}".`)) {
      return;
    }

    try {
      setSaving(true);
      await api.applyTemplate(templateId);
      setSelectedSlots([]);
      await loadSchedules();
    } catch (err) {
      console.error('Failed to apply template:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Kas oled kindel? See lähtestab kõik ajapesad vaikimisi seadetele (Omatarve).')) {
      return;
    }

    try {
      setSaving(true);
      await api.resetSchedules();
      setSelectedSlots([]);
      await loadSchedules();
    } catch (err) {
      console.error('Failed to reset schedules:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <Header
        title="Ajakava"
        connected={wsConnected}
        onRefresh={loadSchedules}
      />

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {/* Status bar */}
        <div className="card p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calendar className="w-6 h-6 text-primary-400" />
            <div>
              <div className="text-white font-medium">Ajakava haldur</div>
              <div className="text-sm text-gray-400">
                {data.enabled ? 'Aktiivne' : 'Peatatud'} - 96 ajapesa (15 min)
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Reset button */}
            <button
              onClick={handleReset}
              disabled={saving}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={clsx('w-4 h-4', saving && 'animate-spin')} />
              Lähtesta
            </button>

            {/* Scheduler toggle */}
            <button
              onClick={toggleScheduler}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                data.enabled
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              )}
            >
              {data.enabled ? (
                <>
                  <ToggleRight className="w-5 h-5" />
                  Sees
                </>
              ) : (
                <>
                  <ToggleLeft className="w-5 h-5" />
                  Väljas
                </>
              )}
            </button>
          </div>
        </div>

        {/* Current status */}
        <CurrentStatus
          currentSlot={activeData.active}
          decision={activeData.decision}
          soc={activeData.currentSoc}
        />

        {/* Time slot grid */}
        {loading && !data.timeSlots.length ? (
          <div className="card p-8 text-center text-gray-400">
            Laadin ajakava...
          </div>
        ) : (
          <TimeSlotGrid
            slots={data.timeSlots}
            currentSlotIndex={data.currentSlotIndex}
            selectedSlots={selectedSlots}
            onSlotClick={handleSlotClick}
            onSlotDragStart={handleSlotDragStart}
            onSlotDragEnter={handleSlotDragEnter}
            onSlotDragEnd={handleSlotDragEnd}
          />
        )}

        {/* Slot editor */}
        {selectedSlots.length > 0 && (
          <SlotEditor
            selectedSlots={selectedSlots}
            slots={data.timeSlots}
            onSave={handleSaveSlots}
            onCancel={() => setSelectedSlots([])}
          />
        )}

        {/* Templates */}
        <TemplateSelector onApply={handleApplyTemplate} loading={saving} />

        {/* Help text */}
        <div className="text-sm text-gray-500 text-center">
          Klõpsa ajapesal selle valimiseks. Lohista mitme pesa valimiseks. Valitud pesade seadeid saad muuta allpool.
        </div>
      </div>
    </div>
  );
}

export default SchedulePage;
